/**
 * PlatformSettings.tsx — PlatformOwner-only editing of `app.platform_setting` (PC-F20), reskinned to
 * the teal "upload" design system and mounted as an in-place modal panel over AdminHome
 * (PlatformSettingsPanel), rather than the old full-page navy screen at /admin/settings. Until PC-F20
 * every value here was read-only/seed-only: no write function, no route, no UI anywhere. Grouped by
 * owning_doc; one control per value_type.
 *
 * It also carries the WORKFORCE HEADCOUNT panel (MD-F04). Headcount is the one dashboard input the
 * platform does not and must not compute — "supplied, never derived" (migration 0013) — because the
 * obvious derivation, counting `app_user` rows, answers "who has signed in" and would quietly
 * overstate every per-head percentage. It belongs on this screen rather than the dashboard for two
 * reasons: the dashboard writes nothing (MD-NFR09), and recording a denominator is a governance act
 * attributed to the platform owner (PC-F18), not a reporting one.
 *
 * The panel fetches its own data on open via useOverlayData (AdminHome does not preload it) and, after
 * a save, calls reload() to refresh in place instead of a router revalidation. The still-registered
 * /admin/settings route renders through a thin PlatformSettings() wrapper that opens the same panel
 * over a blank backdrop for the deep-link path.
 *
 * Provisional engineering UI — matches the other admin governance panels' own posture.
 */
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { MODERATION_MODES } from '@trailblazer/shared';
import { apiGet, apiPatch, type PlatformSettingRow, type PlatformSettingValueType } from './api';
import { apiPut } from '../community/api';
import { shortWhen } from './shared';
import {
  EmptyState,
  ErrorLine,
  KombiAdminOverlay,
  NotPermitted,
  Notice,
  PrimaryAction,
  ProvisionalNote,
  SectionHeader,
  Spinner,
} from '../kombi/admin/chrome';
import { useOverlayData, type ReturnFocusRef } from './overlay';

/**
 * A few string settings have a KNOWN, closed set of valid values, so a free-text box invites typos
 * that silently misbehave (a mistyped `moderation_mode` falls back to manual_review — safe, but
 * confusing). For those keys we render a dropdown of exactly the allowed values instead of a text
 * input. Any key NOT listed here keeps its normal control (text / bool / object) unchanged.
 *
 * Each option carries a plain-language label for the owner; the VALUE saved is always the raw
 * vocabulary string, so this is purely a friendlier way to pick the same value the text box took.
 * The moderation_mode options are built FROM the shared MODERATION_MODES vocabulary (not a hand-
 * copied list), so they cannot drift from what the server accepts and a new mode added there shows
 * up here automatically with a sensible default label.
 */
const MODERATION_MODE_LABELS: Record<(typeof MODERATION_MODES)[number], string> = {
  manual_review: 'Manual review required — every submission is held for a reviewer',
  self_moderated: 'Self-moderated — a submission that passes all checks publishes with no review',
};

const CHOICE_SETTINGS: Record<string, { value: string; label: string }[] | undefined> = {
  moderation_mode: MODERATION_MODES.map((mode) => ({
    value: mode,
    label: MODERATION_MODE_LABELS[mode] ?? mode,
  })),
};

/** The per-row status/muted message span. */
/**
 * One recorded headcount, mirroring `HeadcountRecord` in packages/api/src/db/headcount.ts as the
 * GET returns it. Hand-written like every other web type here, so it has to be checked against the
 * server rather than assumed: `headcount` arrives already `Number()`-coerced off the BIGINT/INT
 * boundary, and `recordedAt` is an ISO 8601 string, not a Date.
 */
export interface HeadcountRecord {
  scope: string;
  headcount: number;
  recordedAt: string;
}

export type PlatformSettingsLoaderData =
  | { state: 'signedOut' }
  | { state: 'forbidden' }
  | {
      state: 'ok';
      settings: PlatformSettingRow[];
      headcounts: HeadcountRecord[];
      /** Every scope a headcount MAY be recorded against — 'ALL' plus each live governed department. */
      scopes: string[];
    };

export async function platformSettingsLoader(): Promise<PlatformSettingsLoaderData> {
  // Both reads sit behind the same OWNER_ONLY gate on the same router, so they go together and one
  // gate decision covers the whole screen. The scopes come back WITH the recorded rows deliberately:
  // the form must never invent its own department list, or it would offer a scope no dashboard filter
  // can ever match.
  const [settingsRes, headcountRes] = await Promise.all([
    apiGet<{ settings: PlatformSettingRow[] }>('/api/admin/settings'),
    apiGet<{ headcounts: HeadcountRecord[]; scopes: string[] }>('/api/admin/settings/headcount'),
  ]);
  if (settingsRes.kind === 'unauthorized' || headcountRes.kind === 'unauthorized')
    return { state: 'signedOut' };
  if (settingsRes.kind === 'error') {
    if (settingsRes.status === 403) return { state: 'forbidden' };
    throw new Error(`GET /api/admin/settings failed (${settingsRes.status})`);
  }
  if (headcountRes.kind === 'error') {
    if (headcountRes.status === 403) return { state: 'forbidden' };
    throw new Error(`GET /api/admin/settings/headcount failed (${headcountRes.status})`);
  }
  return {
    state: 'ok',
    settings: settingsRes.body.settings,
    headcounts: headcountRes.body.headcounts,
    scopes: headcountRes.body.scopes,
  };
}

/**
 * The outcome of reading the editor's text back into the value_type's real shape.
 *
 * A DISCRIMINATED result rather than `unknown | null`, because `null` is a value a row can actually
 * hold — `value` is JSONB, and the JSON literal `null` sits in it as happily as a number does — so
 * the old `null`-means-failure signature could not tell "the operator typed nonsense" apart from
 * "this parsed to null". That conflation is exactly how a stored null was mishandled in both
 * directions at once: reported as a JSON parse failure on an `object` row, and silently rewritten as
 * the STRING "null" on a `string` one.
 */
type ParsedEditorValue = { ok: true; value: unknown } | { ok: false; message: string };

/** Split a comma-separated list, dropping only the empty slots a trailing comma leaves behind. */
function splitList(text: string): string[] {
  return text
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * Parse the editor's plain-text representation back into the value_type's real shape. The server is
 * the real validator; this is the friendlier first pass, and it now REJECTS what it cannot represent
 * instead of repairing it:
 *
 *   - a non-numeric entry fails the whole int[]/number[] list, matching `narrowIntArray` /
 *     `narrowNumberArray` on the server (db/settingValues.ts), which reject an array whole. The
 *     previous code FILTERED the bad entry out — while its own docstring claimed it returned null —
 *     so a mistyped list wrote a SHORTER list than the one on screen and reported "Saved.";
 *   - a `bool` box holding anything other than true/false is an error, not `false`. `text === 'true'`
 *     meant a row whose stored value was a null (or a mis-typed string) saved itself AS false on the
 *     first click of Save, with nothing on screen having been touched.
 */
function parseEditorValue(text: string, valueType: PlatformSettingValueType): ParsedEditorValue {
  switch (valueType) {
    case 'bool':
      if (text === 'true') return { ok: true, value: true };
      if (text === 'false') return { ok: true, value: false };
      return { ok: false, message: 'Choose true or false.' };
    case 'int': {
      const trimmed = text.trim();
      const n = Number(trimmed);
      if (trimmed.length === 0 || !Number.isInteger(n))
        return { ok: false, message: 'That value must be a whole number.' };
      return { ok: true, value: n };
    }
    case 'int[]':
    case 'number[]': {
      const whole = valueType === 'int[]';
      const nums = splitList(text).map((s) => Number(s));
      if (nums.some((n) => !Number.isFinite(n) || (whole && !Number.isInteger(n))))
        return {
          ok: false,
          message: whole
            ? 'Every entry must be a whole number, separated by commas.'
            : 'Every entry must be a number, separated by commas.',
        };
      return { ok: true, value: nums };
    }
    case 'string[]':
      return { ok: true, value: splitList(text) };
    case 'object':
      try {
        // JSON.parse('null') is a SUCCESSFUL parse of the value null, and is reported as one. Whether
        // a null may be WRITTEN is a separate question, answered in save().
        return { ok: true, value: JSON.parse(text) as unknown };
      } catch {
        return { ok: false, message: 'Could not parse that value as JSON.' };
      }
    case 'cron':
    case 'string':
    default:
      return { ok: true, value: text };
  }
}

/**
 * The editor's plain-text representation of a stored value, for the initial input contents.
 *
 * A STORED NULL RENDERS AS EMPTY, never as the four characters "null". `String(null)` — what this
 * used to do for every non-object type — put the literal text `null` in the box, and parsing that
 * back on a `string` row handed the server the STRING "null": opening this page and pressing Save,
 * with nothing edited, was enough to convert a real null into a string that reads identically in a
 * log and is a different value to every narrower on the read path. An empty box is honest about
 * there being nothing stored, and SettingRow says so in words beside it.
 *
 * The representation is LOSSY by nature — a string[] element containing a comma cannot survive a
 * comma-joined round trip, and neither can a string with meaningful leading whitespace — which is
 * why SettingRow submits the loaded value ITSELF whenever the text has not been edited, rather than
 * the parse of this rendering.
 */
function toEditorText(value: unknown, valueType: PlatformSettingValueType): string {
  if (value === null || value === undefined) return '';
  if (valueType === 'object') return JSON.stringify(value, null, 2);
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

function SettingRow({ setting, onSaved }: { setting: PlatformSettingRow; onSaved: () => void }) {
  // The text this row LOADED with. `text === loadedText` is what makes an untouched Save a no-op:
  // toEditorText is lossy, so re-parsing a rendering nobody edited can hand the server a DIFFERENT
  // value than the one it sent. When nothing has been edited we submit `setting.value` itself, so
  // what is written back is byte-identical to what was loaded whatever its shape.
  const loadedText = toEditorText(setting.value, setting.valueType);
  const [text, setText] = useState(loadedText);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const storedIsNull = setting.value === null || setting.value === undefined;
  const untouched = text === loadedText;

  // A known-values setting (e.g. moderation_mode) gets a dropdown of exactly its allowed values,
  // so the owner picks rather than types — no typo can misroute the platform. Everything else keeps
  // its normal control below. Only string-typed settings are eligible (the choices are raw strings).
  const choices = setting.valueType === 'string' ? CHOICE_SETTINGS[setting.key] : undefined;

  // If the stored value isn't one of the known choices (a legacy or hand-mistyped value), a plain
  // <select> would misleadingly HIGHLIGHT its first option while a different value is actually saved.
  // Surface the truth instead: prepend a disabled "current (unrecognised)" entry showing the real
  // stored value, so the owner sees it must be changed to a valid one. Never added when the stored
  // value already matches a choice.
  const storedIsKnownChoice = !!choices && choices.some((c) => c.value === text);
  const unrecognisedStored = choices && !storedIsKnownChoice ? text : null;

  async function save() {
    setBusy(true);
    setMessage(null);
    try {
      const parsed: ParsedEditorValue = untouched
        ? { ok: true, value: setting.value }
        : parseEditorValue(text, setting.valueType);
      if (!parsed.ok) {
        setMessage(parsed.message);
        return;
      }
      // Every one of the eight narrowers behind the write path rejects null (db/settingValues.ts:
      // narrowBool/narrowInt/narrowString/narrowCron all type-test, and Array.isArray(null) is
      // false), so forwarding a null could only ever come back 400 invalid_value. Say why in words
      // instead — and in particular do not let an untouched null row LOOK like a saveable edit.
      if (parsed.value === null || parsed.value === undefined) {
        setMessage(
          storedIsNull && untouched
            ? 'Nothing is stored for this setting, so there is nothing to save — the product is reading its documented default. Type the value you want first.'
            : 'A null is not a value a setting can hold: the write path rejects one for every value_type. Type a real value.',
        );
        return;
      }
      const res = await apiPatch<{ ok: true }>(
        `/api/admin/settings/${encodeURIComponent(setting.key)}`,
        { value: parsed.value, valueType: setting.valueType },
      );
      if (res.kind === 'ok') {
        setMessage('Saved.');
        onSaved();
        return;
      }
      setMessage(
        `Failed (${res.kind === 'error' ? res.status : 'unauthorized'}${res.kind === 'error' && res.code ? `: ${res.code}` : ''}).`,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="kombi-admin-card">
      <div className="kombi-admin-value">{setting.key}</div>
      <p className="learn-note">
        {setting.description} · {setting.valueType}
        {storedIsNull && ' · no value stored'}
      </p>
      {/* `.kombi-field` styles whichever control this row's valueType calls for; there is no <span>
          because the setting's own key and description are the label, above. */}
      <div className="kombi-field">
        {choices ? (
          <select onChange={(e) => setText(e.target.value)} value={text}>
            {unrecognisedStored !== null && (
              <option value={unrecognisedStored} disabled>
                {`current (unrecognised): ${unrecognisedStored} — choose a valid option`}
              </option>
            )}
            {choices.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        ) : setting.valueType === 'bool' ? (
          <select onChange={(e) => setText(e.target.value)} value={text}>
            {/* A bool row whose stored value is not a real boolean — a null, or a mis-typed string —
                has nothing to select. Offer its own current text as the selected option rather than
                letting the browser default the box to "true", which would show the owner a value the
                platform is not using and make one click of Save write it. */}
            {text !== 'true' && text !== 'false' && (
              <option value={text}>
                {text === '' ? '(no value stored)' : `${text} (not a boolean)`}
              </option>
            )}
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        ) : setting.valueType === 'object' ? (
          <textarea onChange={(e) => setText(e.target.value)} value={text} />
        ) : (
          <input onChange={(e) => setText(e.target.value)} type="text" value={text} />
        )}
      </div>
      <div className="kombi-admin-row-actions">
        <PrimaryAction disabled={busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save'}
        </PrimaryAction>
        {message && <span className="learn-note">{message}</span>}
      </div>
    </li>
  );
}

/**
 * MD-F04's write surface. Until this existed the table had a writer at the API and no caller in the
 * product, so every per-head figure on the management dashboard reported `headcount-not-recorded`
 * however long the platform ran — the state MD-F04 exists to end.
 *
 * A SELECT, not a text box, for the scope: a typo would create a scope no dashboard filter can ever
 * match, and the server refuses anything that is not 'ALL' or a governed department label anyway (404
 * `unknown_department`). The list is the server's own, fetched with the recorded rows, and it holds only
 * LIVE departments. A RETIRED one is still accepted by the write path — a figure recorded before a
 * department was retired stays correctable — it is simply not offered here as a new choice.
 *
 * PUT, not PATCH, and the panel says so in its copy: a second submission for the same scope
 * re-records rather than adding a row (0013 gives the table one row per scope), and `recorded_at`
 * moves to now() each time so a stale denominator is visible AS stale rather than silently ageing.
 */
function HeadcountPanel({
  headcounts,
  scopes,
  onSaved,
}: {
  headcounts: HeadcountRecord[];
  scopes: string[];
  onSaved: () => void;
}) {
  const [scope, setScope] = useState(scopes[0] ?? 'ALL');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const recordedFor = new Map(headcounts.map((h) => [h.scope, h]));
  const existing = recordedFor.get(scope);

  async function save() {
    setBusy(true);
    setMessage(null);
    try {
      // Parsed here only to give a friendlier first pass than a 400 — the route's zod schema
      // (positive integer) and 0013's CHECK are the real validators.
      const headcount = Number.parseInt(text.trim(), 10);
      if (!Number.isInteger(headcount) || headcount <= 0) {
        setMessage('A headcount must be a whole number greater than zero.');
        return;
      }
      const res = await apiPut<{ ok: true }>('/api/admin/settings/headcount', { scope, headcount });
      if (res.kind === 'ok') {
        setText('');
        setMessage(
          `Recorded ${headcount.toLocaleString()} for ${scope}. Per-head figures on the management dashboard now have a denominator for this scope.`,
        );
        onSaved();
        return;
      }
      if (res.kind === 'unauthorized') {
        setMessage('Your session has ended. Sign in again to record a headcount.');
        return;
      }
      setMessage(
        res.code === 'unknown_department'
          ? 'That scope is not a live governed department. Only “ALL” or a current department may hold a headcount.'
          : res.code === 'invalid_request'
            ? 'A headcount must be a whole number greater than zero.'
            : `Failed (${res.status}${res.code ? `: ${res.code}` : ''}).`,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <SectionHeader>Workforce headcount</SectionHeader>
      <Notice>
        The management dashboard reports adoption and mandatory-training completion as a share of
        the workforce. That denominator is <strong>supplied here, never derived</strong> (MD-F04):
        counting accounts would answer “who has signed in”, which is a smaller and different number.
        Until a scope has a headcount, every figure that needs it says so plainly rather than
        guessing.
      </Notice>

      <div className="kombi-admin-card">
        <div className="kombi-dialog-stack">
          <label className="kombi-field" htmlFor="headcount-scope">
            <span>Scope</span>
            <select id="headcount-scope" onChange={(e) => setScope(e.target.value)} value={scope}>
              {scopes.map((s) => (
                <option key={s} value={s}>
                  {s === 'ALL' ? 'ALL (whole platform)' : s}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor="headcount-value" className="kombi-field">
            <span>People in this scope</span>
            <input
              id="headcount-value"
              type="number"
              min={1}
              step={1}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={existing ? String(existing.headcount) : 'e.g. 60'}
            />
          </label>
        </div>
        <p className="learn-note">
          {existing
            ? `Currently ${existing.headcount.toLocaleString()}, recorded ${shortWhen(existing.recordedAt)}. Saving replaces it and re-dates it.`
            : 'Nothing recorded for this scope yet.'}
        </p>
        <div className="kombi-admin-row-actions">
          <PrimaryAction disabled={busy} onClick={() => void save()}>
            {busy ? 'Recording…' : existing ? 'Update headcount' : 'Record headcount'}
          </PrimaryAction>
          {message && <span className="learn-note">{message}</span>}
        </div>
      </div>

      <SectionHeader>Recorded headcounts</SectionHeader>
      {headcounts.length === 0 ? (
        <EmptyState>
          No headcount has been recorded. Every per-head figure on the management dashboard is
          reporting “headcount not recorded” until one is.
        </EmptyState>
      ) : (
        <ul className="kombi-dialog-stack">
          {headcounts.map((h) => (
            <li key={h.scope} className="kombi-admin-card">
              <div>
                {h.scope === 'ALL' ? 'ALL (whole platform)' : h.scope}:{' '}
                {h.headcount.toLocaleString()} people
              </div>
              <p className="learn-note">Recorded {shortWhen(h.recordedAt)}.</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The overlay body AdminHome mounts. Fetches its own data on open (useOverlayData →
 * platformSettingsLoader), shows the pending line while loading, an ErrorLine for a failed load / ended
 * session, NotPermitted for a forbidden result, and the headcount panel + grouped settings
 * on success — every save reload()s in place.
 */
export function PlatformSettingsPanel({
  onClose,
  returnFocusRef: _returnFocusRef,
}: {
  onClose: () => void;
  returnFocusRef: ReturnFocusRef;
}) {
  const { data, loading, error, reload } = useOverlayData(platformSettingsLoader);

  const byDoc = new Map<string, PlatformSettingRow[]>();
  if (data && data.state === 'ok') {
    for (const s of data.settings) {
      const group = byDoc.get(s.owningDoc) ?? [];
      group.push(s);
      byDoc.set(s.owningDoc, group);
    }
  }

  return (
    <KombiAdminOverlay title="Platform Settings" onClose={onClose}>
      {loading ? (
        <Spinner label="Loading platform settings…" />
      ) : error || (data && data.state === 'signedOut') ? (
        // signedOut inside an overlay: don't navigate — the parent grid owns auth. Just say so.
        <ErrorLine>
          Your session has ended or the platform settings could not be loaded. Close this panel and
          sign in again.
        </ErrorLine>
      ) : data && data.state === 'forbidden' ? (
        <NotPermitted what="Platform Settings" />
      ) : data && data.state === 'ok' ? (
        <div>
          <ProvisionalNote />
          <Notice>
            Every product parameter the platform owner tunes with no deploy (PC-F20), grouped by the
            document that owns it.
          </Notice>

          {/* Headcount leads, above the tunable parameters: it is the one value on this screen with no
              default and no seed, and nothing on the dashboard that needs it works until it is here. */}
          <HeadcountPanel headcounts={data.headcounts} scopes={data.scopes} onSaved={reload} />

          {[...byDoc.entries()].map(([doc, settings]) => (
            <div key={doc}>
              <SectionHeader>{doc}</SectionHeader>
              <ul className="kombi-dialog-stack">
                {settings.map((s) => (
                  <SettingRow key={s.key} setting={s} onSaved={reload} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
    </KombiAdminOverlay>
  );
}

/**
 * Thin route wrapper for the still-registered /admin/settings deep link. Opens the panel always-open
 * over a blank backdrop; the panel still fetches via useOverlayData, and Close returns to /admin.
 */
export function PlatformSettings() {
  const navigate = useNavigate();
  const ref = useRef<HTMLElement | null>(null);
  return <PlatformSettingsPanel onClose={() => navigate('/admin')} returnFocusRef={ref} />;
}
